import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Check, X, ArrowLeft, Save, RotateCcw, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Image Manifest — admin-only visual review tool.
 *
 * Shows every candidate exercise image in a grid, grouped by muscle. Click any
 * image to toggle keep ✓ / kill ✗. State persists in localStorage so you can
 * step away and come back. When ready, click "Apply" and the page POSTs a
 * single payload to /api/admin/image-manifest with the keep/kill verdicts.
 *
 * The backend then:
 *   - deletes the killed files from /public
 *   - writes a manifest JSON the frontend uses for getExerciseImage(muscle)
 *
 * Until you click Apply, nothing is touched on disk.
 */

const LS_KEY = 'enferno.image_manifest.verdicts.v1';

// Curated list — every entry MUST exist on disk. Generated by directory
// scan against /public/generated/strength-*.png + /public/library/categories/*.
// If you add a new image, append it here with its primary muscle category.
const CANDIDATES = [
  // NanoBanana strength set — only files that actually exist on disk
  // arms
  { muscle: 'arms', src: '/generated/strength-arms.png', set: 'nanobanana' },
  { muscle: 'arms', src: '/generated/strength-arms-v2.png', set: 'nanobanana' },
  { muscle: 'arms', src: '/generated/strength-arms-v3.png', set: 'nanobanana' },
  // back
  { muscle: 'back', src: '/generated/strength-back.png', set: 'nanobanana' },
  { muscle: 'back', src: '/generated/strength-back-v2.png', set: 'nanobanana' },
  { muscle: 'back', src: '/generated/strength-back-v3.png', set: 'nanobanana' },
  // calves
  { muscle: 'calves', src: '/generated/strength-calves-v1.png', set: 'nanobanana' },
  { muscle: 'calves', src: '/generated/strength-calves-v2.png', set: 'nanobanana' },
  { muscle: 'calves', src: '/generated/strength-calves-v3.png', set: 'nanobanana' },
  // chest
  { muscle: 'chest', src: '/generated/strength-chest.png', set: 'nanobanana' },
  { muscle: 'chest', src: '/generated/strength-chest-v2.png', set: 'nanobanana' },
  { muscle: 'chest', src: '/generated/strength-chest-v3.png', set: 'nanobanana' },
  // core
  { muscle: 'core', src: '/generated/strength-core.png', set: 'nanobanana' },
  { muscle: 'core', src: '/generated/strength-core-v2.png', set: 'nanobanana' },
  { muscle: 'core', src: '/generated/strength-core-v3.png', set: 'nanobanana' },
  // forearms
  { muscle: 'forearms', src: '/generated/strength-forearms-v1.png', set: 'nanobanana' },
  { muscle: 'forearms', src: '/generated/strength-forearms-v2.png', set: 'nanobanana' },
  { muscle: 'forearms', src: '/generated/strength-forearms-v3.png', set: 'nanobanana' },
  // glutes
  { muscle: 'glutes', src: '/generated/strength-glutes-v1.png', set: 'nanobanana' },
  { muscle: 'glutes', src: '/generated/strength-glutes-v2.png', set: 'nanobanana' },
  { muscle: 'glutes', src: '/generated/strength-glutes-v3.png', set: 'nanobanana' },
  // hamstrings
  { muscle: 'hamstrings', src: '/generated/strength-hamstrings-v1.png', set: 'nanobanana' },
  { muscle: 'hamstrings', src: '/generated/strength-hamstrings-v2.png', set: 'nanobanana' },
  { muscle: 'hamstrings', src: '/generated/strength-hamstrings-v3.png', set: 'nanobanana' },
  // legs
  { muscle: 'legs', src: '/generated/strength-legs.png', set: 'nanobanana' },
  { muscle: 'legs', src: '/generated/strength-legs-v2.png', set: 'nanobanana' },
  { muscle: 'legs', src: '/generated/strength-legs-v3.png', set: 'nanobanana' },
  { muscle: 'legs', src: '/generated/strength-quads.png', set: 'nanobanana' },
  { muscle: 'legs', src: '/generated/strength-lower.png', set: 'nanobanana' },
  // shoulders
  { muscle: 'shoulders', src: '/generated/strength-shoulders.png', set: 'nanobanana' },
  { muscle: 'shoulders', src: '/generated/strength-shoulders-v3.png', set: 'nanobanana' },
  // full-body
  { muscle: 'full-body', src: '/generated/strength-fullbody.png', set: 'nanobanana' },
  { muscle: 'full-body', src: '/generated/strength-fullbody-v2.png', set: 'nanobanana' },
  { muscle: 'full-body', src: '/generated/strength-fullbody-v3.png', set: 'nanobanana' },
  { muscle: 'full-body', src: '/generated/strength-upper.png', set: 'nanobanana' },
  { muscle: 'full-body', src: '/generated/strength-posterior.png', set: 'nanobanana' },

  // Your recent uploads (May 29)
  { muscle: 'arms', src: '/library/categories/arms.jpg', set: 'founder-upload' },
  { muscle: 'back', src: '/library/categories/back.jpg', set: 'founder-upload' },
  { muscle: 'calves', src: '/library/categories/calves.jpg', set: 'founder-upload' },
  { muscle: 'chest', src: '/library/categories/chest.jpg', set: 'founder-upload' },
  { muscle: 'core', src: '/library/categories/core.jpg', set: 'founder-upload' },
  { muscle: 'forearms', src: '/library/categories/forearms.jpg', set: 'founder-upload' },
  { muscle: 'glutes', src: '/library/categories/glutes.jpg', set: 'founder-upload' },
  { muscle: 'hamstrings', src: '/library/categories/hamstrings.jpg', set: 'founder-upload' },
  { muscle: 'legs', src: '/library/categories/legs.jpg', set: 'founder-upload' },
  { muscle: 'neck', src: '/library/categories/neck.jpg', set: 'founder-upload' },
  { muscle: 'shoulders', src: '/library/categories/shoulders.jpg', set: 'founder-upload' },
  { muscle: 'full-body', src: '/library/categories/full-body.jpg', set: 'founder-upload' },
];

const MUSCLE_ORDER = ['chest', 'back', 'shoulders', 'arms', 'core', 'legs', 'glutes', 'hamstrings', 'calves', 'forearms', 'neck', 'full-body'];

const ImageManifest = () => {
  const { user } = useAuth();
  const [verdicts, setVerdicts] = useState({}); // { [src]: 'keep' | 'kill' | undefined (= unreviewed) }
  const [brokenImages, setBrokenImages] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null); // { deleted_count, skipped, error? }

  // Hydrate verdicts from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) setVerdicts(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(verdicts)); } catch { /* ignore */ }
  }, [verdicts]);

  const setVerdict = (src, v) => setVerdicts((prev) => {
    const next = { ...prev };
    if (prev[src] === v) delete next[src]; // clicking same again clears
    else next[src] = v;
    return next;
  });

  const resetAll = () => {
    if (window.confirm('Clear every keep/kill choice you have made?')) setVerdicts({});
  };

  const applyKills = async () => {
    const kill = Object.entries(verdicts).filter(([, v]) => v === 'kill').map(([src]) => src);
    if (kill.length === 0) return;
    if (!window.confirm(`Permanently delete ${kill.length} image${kill.length === 1 ? '' : 's'} from disk?\n\nThis cannot be undone.`)) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await axios.post(`${API}/admin/image-manifest/apply`, { kill });
      setApplyResult({ ok: true, ...res.data });
      // Drop the kill verdicts from local state so the deleted ones don't keep showing as "kill"
      setVerdicts((prev) => {
        const next = {};
        for (const [k, v] of Object.entries(prev)) {
          if (v !== 'kill') next[k] = v;
        }
        return next;
      });
    } catch (err) {
      setApplyResult({ ok: false, error: err?.response?.data?.detail || err?.message || 'Apply failed.' });
    } finally {
      setApplying(false);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const c of CANDIDATES) {
      if (!g[c.muscle]) g[c.muscle] = [];
      g[c.muscle].push(c);
    }
    return g;
  }, []);

  const stats = useMemo(() => {
    const keep = Object.values(verdicts).filter((v) => v === 'keep').length;
    const kill = Object.values(verdicts).filter((v) => v === 'kill').length;
    const unreviewed = CANDIDATES.length - keep - kill;
    return { keep, kill, unreviewed, total: CANDIDATES.length };
  }, [verdicts]);

  if (user?.role !== 'admin') {
    return (
      <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center text-enf-text-secondary">
          Admin only.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        <Link to="/admin" className="inline-flex items-center gap-2 text-enf-text-secondary hover:text-enf-text-primary text-sm mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Fire Station
        </Link>
        <div className="mb-6">
          <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-1">Library imagery</div>
          <h1 className="font-display text-5xl tracking-wide leading-none">Image manifest review.</h1>
          <p className="text-enf-text-secondary mt-3 text-sm leading-relaxed max-w-2xl">
            Every candidate exercise image, grouped by muscle. Click an image once to mark it <span className="text-adaptive-sage font-semibold">Keep</span>. Click again to clear. Click the red X to mark <span className="text-enf-accent-fg font-semibold">Kill</span>. Nothing is touched on disk until you click <span className="font-semibold">Apply</span> at the bottom.
          </p>
        </div>

        {/* Sticky stats bar */}
        <div className="sticky top-16 z-30 bg-black/95 backdrop-blur border-b border-enf-surface-border -mx-6 px-6 py-3 mb-6 flex items-center justify-between gap-3">
          <div className="text-xs text-enf-text-muted flex flex-wrap items-center gap-x-4 gap-y-1">
            <span><span className="text-adaptive-sage font-semibold">{stats.keep}</span> keep</span>
            <span><span className="text-enf-accent-fg font-semibold">{stats.kill}</span> kill</span>
            <span><span className="text-enf-text-secondary font-semibold">{stats.unreviewed}</span> unreviewed</span>
            <span className="text-enf-text-muted">/</span>
            <span>{stats.total} total</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={resetAll}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-xs text-enf-text-secondary transition-colors"
              data-testid="manifest-reset">
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
            <button
              disabled={applying || stats.kill === 0}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-xs font-semibold disabled:opacity-50 transition-colors"
              data-testid="manifest-apply"
              onClick={applyKills}
            >
              <Save className="w-3 h-3" /> {applying ? 'Deleting…' : `Delete ${stats.kill} kill${stats.kill === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>

        {/* Apply result toast (inline) */}
        {applyResult && (
          <div
            className={`mb-6 p-4 rounded-md border flex items-start gap-3 ${
              applyResult.ok
                ? 'bg-adaptive-sage/10 border-adaptive-sage/30 text-adaptive-sage'
                : 'bg-enf-accent/10 border-enf-accent-fg/30 text-enf-accent-fg'
            }`}
            data-testid="manifest-apply-result"
          >
            {applyResult.ok ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
            <div className="text-sm">
              {applyResult.ok ? (
                <>
                  <div className="font-semibold mb-1">Deleted {applyResult.deleted_count} file{applyResult.deleted_count === 1 ? '' : 's'}.</div>
                  {applyResult.skipped?.length > 0 && (
                    <div className="text-xs opacity-80">
                      {applyResult.skipped.length} skipped: {applyResult.skipped.map((s) => `${s.path} (${s.reason})`).join(', ')}
                    </div>
                  )}
                </>
              ) : (
                <div className="font-semibold">Apply failed: {applyResult.error}</div>
              )}
            </div>
          </div>
        )}

        {MUSCLE_ORDER.filter((m) => grouped[m]?.length).map((muscle) => (
          <div key={muscle} className="mb-10" data-testid={`manifest-muscle-${muscle}`}>
            <div className="flex items-baseline gap-3 mb-3 border-b border-enf-surface-border pb-2">
              <h2 className="font-display text-2xl tracking-wide capitalize">{muscle.replace('-', ' ')}</h2>
              <span className="text-xs text-enf-text-muted">{grouped[muscle].length} candidate{grouped[muscle].length === 1 ? '' : 's'}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {grouped[muscle].map((c) => {
                const verdict = verdicts[c.src];
                const broken = brokenImages.has(c.src);
                const borderClass =
                  verdict === 'keep' ? 'border-adaptive-sage ring-2 ring-adaptive-sage/40' :
                  verdict === 'kill' ? 'border-enf-accent-fg ring-2 ring-enf-accent/40 opacity-40' :
                  'border-enf-surface-border hover:border-enf-surface-border';
                return (
                  <div
                    key={c.src}
                    className={`relative bg-enf-surface-mid border-2 rounded-lg overflow-hidden group transition-all ${borderClass}`}
                    data-testid={`manifest-card-${c.src.split('/').pop()}`}
                  >
                    {broken ? (
                      <div className="aspect-square bg-enf-surface-deep flex flex-col items-center justify-center text-enf-text-muted text-xs p-3 text-center">
                        <X className="w-5 h-5 mb-2 text-enf-accent-fg" />
                        Image failed to load
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setVerdict(c.src, 'keep')}
                        className="block w-full aspect-square overflow-hidden focus:outline-none"
                        title="Click to mark Keep"
                      >
                        <img
                          src={c.src}
                          alt={c.src}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={() => setBrokenImages((s) => new Set([...s, c.src]))}
                        />
                      </button>
                    )}
                    <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
                      {verdict === 'keep' && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-adaptive-sage text-black text-[9px] font-bold rounded uppercase tracking-wider">
                          <Check className="w-2.5 h-2.5" /> KEEP
                        </span>
                      )}
                      {verdict === 'kill' && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-enf-accent text-enf-text-primary text-[9px] font-bold rounded uppercase tracking-wider">
                          <X className="w-2.5 h-2.5" /> KILL
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setVerdict(c.src, 'kill'); }}
                        className="w-5 h-5 rounded-full bg-black/70 border border-enf-surface-border hover:bg-enf-accent hover:border-enf-accent-fg flex items-center justify-center transition-colors"
                        title="Mark Kill"
                        data-testid={`manifest-kill-${c.src.split('/').pop()}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="px-2 py-1.5 bg-enf-surface-deep border-t border-enf-surface-border">
                      <div className="text-[10px] text-enf-text-muted font-mono truncate">{c.src.split('/').pop()}</div>
                      <div className="text-[9px] text-enf-text-muted uppercase tracking-widest mt-0.5">{c.set}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-12 p-4 bg-enf-surface-deep border border-enf-surface-border rounded-lg text-xs text-enf-text-muted leading-relaxed">
          <p>
            <span className="text-enf-text-secondary font-semibold">Once you've reviewed every candidate</span> (Keep / Kill / unreviewed), tell me to wire the Apply button. I'll then:
          </p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Delete every <span className="text-enf-accent-fg">Kill</span> image from /public</li>
            <li>Build a <code className="bg-enf-surface-mid px-1 py-0.5 rounded">getExerciseImage(muscle)</code> helper that picks from the survivors, randomized per exercise ID when multiple are available</li>
            <li>Migrate all 315 exercise entries to use the helper based on their actual primary muscle</li>
          </ul>
          <p className="mt-3">
            <span className="text-enf-text-secondary font-semibold">Unreviewed images:</span> any image left unreviewed will be kept by default. Killing requires an explicit click.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImageManifest;
